import { lazy } from 'react'

import { useRouteMatch } from 'react-router'

import { Page } from '@webapp/core/components/Page'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { userReportConfig } from '@webapp/modules/reports/config/reportConfigs'
import { useReportUserPresenter } from '@webapp/modules/reports/features/get-report-user/presenters/UseReportUserPresenter'
import { useReportFilterCarryOver } from '@webapp/modules/reports/hooks/useReportFilterCarryOver'
import { type ReportDetailsPageParams } from '@webapp/modules/reports/pages/types'

const ReportDetailViewChunk = lazy(() =>
  import('@webapp/modules/reports/feature-sets/ReportDetailView').then(
    ({ ReportDetailView }) => ({
      default: ReportDetailView,
    })
  )
)

export const OrdersByUserReportDetailsPage = () => {
  const intl = useProcurifyIntl()
  const pageTitle = intl.formatMessage(userReportConfig.titleKey)
  const { url } = useAppRoutes()
  const { withReportFilters } = useReportFilterCarryOver()
  const match = useRouteMatch<ReportDetailsPageParams>()

  const entityId = Number(match.params.id)

  // Breadcrumb entity name resolved independently of the line-item fetch.
  // Same query key and staleTime as the Feature Set's header
  // hook, so its later mount reads the cached entity instead of refetching.
  // An unknown id ends the trail in the not-found copy the FS shows.
  const {
    data: { userFullName },
    states: { isNotFound },
  } = useReportUserPresenter({ userId: entityId })

  const entityCrumbName = isNotFound
    ? intl.formatMessage(userReportConfig.detailNotFoundHeaderKey)
    : (userFullName ?? '')

  return (
    <Page
      pageTitle={pageTitle}
      breadcrumbs={[
        {
          link: withReportFilters(url('OrdersByUserReportPage')),
          name: pageTitle,
        },
        { name: entityCrumbName, escapeCaseFormatting: true },
      ]}
    >
      <ReportDetailViewChunk
        reportConfig={userReportConfig}
        entityId={entityId}
      />
    </Page>
  )
}
